// src/components/AddMilestoneModal.js
import { Col, Row, Select, DatePicker, Form, Input, Modal, Button, Space, Table, message, Dropdown, Menu } from 'antd';
import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import moment from 'moment';
import { useSelector, useDispatch } from 'react-redux';
import { selectAuth } from '@/redux/auth/selectors';
import AutoCompleteAsync from '@/components/MilestoneAutoCompleteAsync';
import {
    CopyOutlined,
    PlusOutlined,
    CloseOutlined,
    SendOutlined,
    SyncOutlined,
    BulbOutlined,
    CommentOutlined
} from '@ant-design/icons';
import { erp } from '@/redux/erp/actions';
import TextArea from 'antd/lib/input/TextArea';
import { makeApiRequest } from './request/apiRequest';
import cryptoHelper from '@/utils/crypto';
import history from '@/utils/history';
import { getObfuscatedPath } from '@/utils/apiRoute';
const { Option } = Select;

// Custom hook for debouncing a function call
const useDebounce = (callback, delay) => {
    const timeoutRef = useRef(null);
    return useCallback((...args) => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => {
            callback(...args);
        }, delay);
    }, [callback, delay]);
};

// Memoized component for displaying activity history item
const MemoizedActivityItem = ({ activity, onShowChangeDetails, users }) => {
    const getIcon = (action) => {
        if (action.includes('created')) {
            return <PlusOutlined style={{ marginRight: '8px' }} />;
        } else if (action.includes('updated')) {
            return <SyncOutlined style={{ marginRight: '8px' }} />;
        }
        return null;
    };
    const getChangesDisplay = (changes) => {
        if (!changes) return null;
        return (
            <div style={{ marginTop: '4px' }}>
                {Object.keys(changes).map(key => (
                    <div key={key}>
                        <a
                            onClick={() => onShowChangeDetails(key, changes[key])}
                            style={{ color: '#1890ff', cursor: 'pointer' }}
                        >
                            {key}
                        </a>{' '}
                        changed
                    </div>
                ))}
            </div>
        );
    };
    const user = users.find((user) => user._id.toString() === activity.user);
    const userName = `${user?.firstName} ${user?.lastName}`;
    const timestamp = moment(activity.timestamp).format('LLL');
    return (
        <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '8px' }}>
            {getIcon(activity.action)}
            <div>
                <div style={{ fontWeight: 'bold' }}>{userName}</div>
                <div>{activity.action}</div>
                <div style={{ fontSize: '12px', color: '#888' }}>{timestamp}</div>
                {getChangesDisplay(activity.changes)}
            </div>
        </div>
    );
};

// Memoized component for displaying comment item
const MemoizedCommentItem = ({ comment, currentUser, getUserRoleName, getUserName }) => {
    const { userRole, altUserRole } = getUserRoleName(comment.user);
    const isInitiativeOwnerComment = userRole === 'Initiative Owner' || altUserRole === 'Initiative Owner';
    return (
        <div style={{ marginBottom: '10px', wordWrap: 'break-word' }}>
            <p style={{ display: 'flex', alignItems: 'center', margin: 0 }}>
                {isInitiativeOwnerComment ? (
                    <BulbOutlined
                        style={{
                            backgroundColor: 'lightyellow',
                            padding: '2px',
                            borderRadius: '3px',
                            marginRight: '5px',
                        }}
                    />
                ) : (
                    <CommentOutlined
                        style={{
                            backgroundColor: 'lightblue',
                            padding: '2px',
                            borderRadius: '3px',
                            marginRight: '5px',
                        }}
                    />
                )}
                <strong>{getUserName(comment.user)}</strong>
            </p>
            {isInitiativeOwnerComment && comment.user === currentUser.id && (
                <p style={{ margin: 0 }}>Justification from IO</p>
            )}
            <p style={{ margin: 0, wordWrap: 'break-word' }}>{comment.text}</p>
            {comment.previousStage && (
                <p style={{ margin: 0 }}>
                    the previous stage is {comment.previousStage}
                </p>
            )}
            <p style={{ margin: 0 }}>
                <em>{new Date(comment.timestamp).toLocaleString()}</em>
            </p>
        </div>
    );
};

const AddMilestoneModal = ({
    isAddMilestoneModalOpen,
    setAddMilestoneModal,
    milestoneDetails = null,
    edit = false,
    initiative,
    setInitiative,
    onMilestoneCreate,
    users
}) => {
    const getUserName = (userId) => {
        const user = users?.find((user) => user._id === userId);
        return user ? `${user.firstName} ${user.lastName}` : 'Unknown User';
    };
    const obfuscatedPath = getObfuscatedPath('user');
    const getUserRoleName = (userId) => {
        const user = users?.find((user) => user._id === userId);
        const userRole = user ? user.role.name : 'Unknown Role';
        const altUserRole = user ? user?.altRole?.name : 'Unknown Role';
        return { userRole, altUserRole };
    };
    const handleAddNewInitiative = () => {
        history.push('/initiative');
    };
    const handleAddNewWorkstreamAction = () => {
        history.push('/workstreamaction');
    };
    const addMenu = (
        <Menu style={{ background: '#253271', color: 'white' }}>
            <Menu.Item key="add-initiative" onClick={handleAddNewInitiative} style={{ color: 'white' }}>
                Add New Initiative
            </Menu.Item>
            <Menu.Item
                key="add-workstream-action"
                onClick={handleAddNewWorkstreamAction}
                style={{ color: 'white' }}
            >
                Add New Workstream Action
            </Menu.Item>
        </Menu>
    );

    const [form] = Form.useForm();
    const currentUser = useSelector(selectAuth);
    // const hasSpecialAccess = currentUser?.hasSpecialAccess === true;
    const isCTORole = currentUser.role?.name === 'CTO';
    const isCTO = isCTORole;
    
    const commentsActivitiesRef = useRef(null);
    const dispatch = useDispatch();
    const [selectedActivityType, setSelectedActivityType] = useState('both');
    const [newComment, setNewComment] = useState('');
    const [saving, setSaving] = useState(false);
    const [visibleChangeDetails, setVisibleChangeDetails] = useState(false);
    const [changeDetails, setChangeDetails] = useState(null);
    const selectedRecord = milestoneDetails;
    
    // ✅ CORRECTED PERMISSION CHECKS
    const isCancelled = milestoneDetails?.status === 'Cancelled';
    const isL2Stage = initiative?.stage === 'L2';
    const isAboveL2 = initiative?.stage === 'L3' || initiative?.stage === 'L4' || initiative?.stage === 'L5';
    
    // Role checks
    const isInitiativeOwner = initiative?.initiativeOwner?._id === currentUser.id;
    const isInitiativeBackupOwner = initiative?.initiativeOwnerBackup?._id === currentUser.id;
    const isInitiativeOwnerOrBackup = isInitiativeOwner || isInitiativeBackupOwner;
    
    const isMilestoneOwner = milestoneDetails?.Owner?._id === currentUser.id ||
        milestoneDetails?.Owner === currentUser.id;
    
    const isWorkstreamLead = initiative?.workstreamLead?._id === currentUser.id;
    
    // ✅ EDIT PERMISSIONS (for existing milestones)
    // CTO can edit ANY field at ANY stage
    const canEditAllFields = isCTO;
    
    // Initiative Owner/Backup can edit all fields ONLY at L2 stage
    const canEditAllFieldsAtL2 = isInitiativeOwnerOrBackup && isL2Stage;
    
    // For editing all fields (Name, Description, Type, Dates, etc.)
    const canEditFullMilestone = (canEditAllFields || canEditAllFieldsAtL2) && !isCancelled;
    
    // Status editing permissions
    const canEditStatus = !isCancelled && (
        isCTO || 
        isInitiativeOwnerOrBackup || 
        isWorkstreamLead ||
        isMilestoneOwner
    );
    
    // Owner field edit permissions
    const canWorkstreamLeadEditOwner = isWorkstreamLead && isAboveL2 && !isCancelled;
    const canInitiativeOwnerEditOwner = isInitiativeOwnerOrBackup && isL2Stage && !isCancelled;
    const canCTOEditOwner = isCTO && !isCancelled;
    
    const canEditOwner = canCTOEditOwner || canInitiativeOwnerEditOwner || canWorkstreamLeadEditOwner;
    
    // ✅ CREATE PERMISSIONS (for new milestones)
    const canCreateMilestone = isCTO || isInitiativeOwnerOrBackup || isWorkstreamLead;
    
    // Field disabled logic based on edit/create mode
    const isFieldDisabled = (fieldName) => {
        // For creating new milestone
        if (!edit) {
            return !canCreateMilestone;
        }
        
        // For editing existing milestone
        if (isCancelled) return true;
        
        switch(fieldName) {
            case 'name':
            case 'description':
            case 'type':
            case 'startDate':
            case 'endDate':
                return !canEditFullMilestone;
            case 'status':
                return !canEditStatus;
            case 'Owner':
                return !canEditOwner;
            case 'planningSummary':
            case 'planningCheck':
                return true;
            default:
                return false;
        }
    };

    // Get the current Owner ID from details for comparison
    const currentOwnerId = useMemo(() => {
        if (!milestoneDetails?.Owner) return null;
        if (typeof milestoneDetails.Owner === 'object' && milestoneDetails.Owner._id) {
            return milestoneDetails.Owner._id.toString();
        }
        return milestoneDetails.Owner.toString();
    }, [milestoneDetails]);

    const getModalTitle = () => {
        if (edit && milestoneDetails && initiative) {
            return `Initiative #${initiative.initiativeNumber} > Milestone #${milestoneDetails.milestoneNumber}`;
        }
        return edit ? 'Milestone Details' : 'Add Milestone';
    };
    
    const user = users?.find((user) => user._id?.toString() === currentOwnerId);

    useEffect(() => {
        if (edit && isAddMilestoneModalOpen && milestoneDetails) {
            form.setFieldsValue({
                name: milestoneDetails.name,
                description: milestoneDetails.description,
                startDate: milestoneDetails.startDate ? moment(milestoneDetails.startDate) : null,
                endDate: milestoneDetails.endDate ? moment(milestoneDetails.endDate) : null,
                status: milestoneDetails.status,
                Owner: user ? user : null,
                type: milestoneDetails.type,
                weight: milestoneDetails.weight,
                planningSummary: milestoneDetails.planningSummary,
                planningCheck: milestoneDetails.planningCheck,
            });
        } else if (!edit) {
            form.resetFields();
            // Set default values for new milestone
            form.setFieldsValue({
                status: 'Not Started',
                type: 'Health',
            });
        }
    }, [edit, isAddMilestoneModalOpen, milestoneDetails, form, user]);

    // Debounced save functions (only for edit mode)
    const saveField = useCallback(async (fieldName, fieldValue) => {
        if (!edit) return;
        setSaving(true);
        const requestData = {
            [fieldName]: fieldValue,
        };
        try {
            const res = await makeApiRequest(
                'patch',
                `/initiative/update/${initiative._id}/milestones/${milestoneDetails._id}`,
                requestData,
                'Milestone updated successfully',
                'Failed to update Milestone'
            );
            if (res?.success) {
                const updatedMilestone = res.result;
                const updatedMilestoneList = Array.isArray(initiative?.milestones)
                    ? initiative.milestones.map((milestone) =>
                        milestone._id === updatedMilestone._id ? updatedMilestone : milestone
                    )
                    : [];
                setInitiative({ ...initiative, milestones: updatedMilestoneList });
            }
        } catch (error) {
            console.error('Error saving field:', error);
        } finally {
            setSaving(false);
        }
    }, [initiative, milestoneDetails, setInitiative, edit]);
    
    const debouncedSaveField = useDebounce(saveField, 500);

    const saveOwner = useCallback(async (ownerId) => {
        if (!edit) return;
        setSaving(true);
        const requestData = {
            Owner: ownerId,
        };
        try {
            const res = await makeApiRequest(
                'patch',
                `/initiative/update/${initiative._id}/milestones/${milestoneDetails._id}`,
                requestData,
                'Milestone Owner updated successfully',
                'Failed to update Milestone Owner'
            );
            if (res?.success) {
                const updatedMilestone = res.result;
                const updatedMilestoneList = Array.isArray(initiative?.milestones)
                    ? initiative.milestones.map((milestone) =>
                        milestone._id === updatedMilestone._id ? updatedMilestone : milestone
                    )
                    : [];
                setInitiative({ ...initiative, milestones: updatedMilestoneList });
            }
        } catch (error) {
            console.error('Error saving owner:', error);
        } finally {
            setSaving(false);
        }
    }, [initiative, milestoneDetails, setInitiative, edit]);
    
    const debouncedSaveOwner = useDebounce(saveOwner, 500);

    const handleFieldChange = (fieldName, value) => {
        if (!edit) return;
        if (moment.isMoment(value)) {
            value = value.toISOString();
        }
        debouncedSaveField(fieldName, value);
    };
    
    const handleStatusChange = (value) => {
        handleFieldChange('status', value);
    };
    
    const handleTypeChange = (value) => {
        handleFieldChange('type', value);
    };
    
    const handleStartDateChange = (date) => {
        handleFieldChange('startDate', date ? date.toISOString() : null);
    };
    
    const handleEndDateChange = (date) => {
        handleFieldChange('endDate', date ? date.toISOString() : null);
    };

    const handleOwnerChange = (newOwnerId) => {
        if (!edit) return;
        if (newOwnerId && newOwnerId.toString() !== currentOwnerId) {
            debouncedSaveOwner(newOwnerId);
        }
    };

    const handleNameChange = (e) => {
        handleFieldChange('name', e.target.value);
    };
    
    const handleDescriptionChange = (e) => {
        handleFieldChange('description', e.target.value);
    };

    const handleAddComment = async () => {
        if (newComment.trim()) {
            const newCommentObj = {
                text: newComment,
                user: currentUser.id,
                timestamp: new Date(),
            };
            const updatedComments = [...(selectedRecord?.comments || []), newCommentObj];
            try {
                const requestData = {
                    comments: updatedComments,
                };
                const res = await makeApiRequest(
                    'put',
                    `/initiative/update/${initiative._id}`,
                    requestData,
                    'Comment created successfully',
                    'Failed to create Comment'
                );
                if (res && res.success) {
                    setNewComment('');
                    dispatch(erp.read({ entity: 'initiative', id: initiative._id }));
                    message.success('Comment added successfully!');
                }
            } catch (error) {
                console.error('Error updating comments:', error);
                message.error('Error adding comment.');
            }
        }
    };

    const showChangeDetailsModal = (fieldName, changeData) => {
        setChangeDetails({ fieldName, ...changeData });
        setVisibleChangeDetails(true);
    };
    
    const handleCloseChangeDetailsModal = () => {
        setVisibleChangeDetails(false);
        setChangeDetails(null);
    };

    const milestoneActivities = useMemo(() => {
        if (!initiative || !initiative.history || !milestoneDetails) {
            return [];
        }
        const milestoneNumber = milestoneDetails.milestoneNumber;
        const filteredHistory = initiative.history.filter(historyItem => {
            const isCreateAction = historyItem.action.includes('Milestone') && historyItem.action.includes('created');
            if (isCreateAction) {
                const newlyAddedMilestoneName = historyItem.action.match(/Milestone #(.+?) created/)?.[1];
                return newlyAddedMilestoneName === milestoneDetails.name;
            }
            const isUpdateAction = historyItem.action.includes('Milestone') && historyItem.action.includes('updated');
            if (isUpdateAction) {
                const updatedMilestoneNumber = historyItem.action.match(/Number\s+(.+?)\s+has/)?.[1];
                return updatedMilestoneNumber === milestoneNumber;
            }
            return false;
        });
        return filteredHistory.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    }, [initiative, milestoneDetails]);

    const getStatusOptions = () => {
        const options = [
            { value: 'Started', label: 'Started' },
            { value: 'Not Started', label: 'Not Started' },
            { value: 'Completed', label: 'Completed' },
        ];
        if (isWorkstreamLead || isCTO) {
            options.push({ value: 'Cancelled', label: 'Cancelled' });
        }
        return options;
    };

    const handleCopyLinkClick = () => {
        const encryptedId = cryptoHelper.encrypt(initiative._id);
        const encodedData = encryptedId.replace(/\//g, '_');
        const currentUrl = `${window.location.origin}/initiative/update/${encodedData}?section=milestones`;
        if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(currentUrl)
                .then(() => {
                    message.success('Link copied to clipboard!');
                })
                .catch(err => {
                    console.error('Failed to copy link: ', err);
                    fallbackCopyToClipboard(currentUrl);
                });
        } else {
            fallbackCopyToClipboard(currentUrl);
        }
    };

    const fallbackCopyToClipboard = (text) => {
        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.top = "0";
        textArea.style.left = "0";
        textArea.style.position = "fixed";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
            const successful = document.execCommand('copy');
            if (successful) {
                message.success('Link copied to clipboard!');
            } else {
                message.error('Failed to copy link');
            }
        } catch (err) {
            console.error('Fallback: Failed to copy link: ', err);
            message.error('Failed to copy link. Please try manually.');
        }
        document.body.removeChild(textArea);
    };

    const handleOk = () => {
        if (!edit) {
            form.validateFields()
                .then((values) => {
                    const payload = {
                        ...values,
                        startDate: values.startDate ? values.startDate.toISOString() : null,
                        endDate: values.endDate ? values.endDate.toISOString() : null,
                        Owner: values.Owner?._id || values.Owner || null,
                        weight: values.weight || 0,
                        initiativeId: initiative._id,
                    };
                    onMilestoneCreate({
                        ...payload,
                        edit: false,
                    });
                    setAddMilestoneModal(false);
                    form.resetFields();
                })
                .catch((err) => console.error('Form validation error:', err));
        } else {
            // In edit mode, changes are auto-saved, just close the modal
            setAddMilestoneModal(false);
            form.resetFields();
        }
    };

    return (
        <Modal
            centered
            width="100%"
            style={{
                top: 0,
                padding: 0,
                margin: 0,
                maxWidth: '100%',
                height: '100vh'
            }}
            bodyStyle={{
                padding: '24px',
                height: 'calc(100vh - 55px)',
                overflowY: 'auto'
            }}
            visible={isAddMilestoneModalOpen}
            title={null}
            footer={null}
            closable={false}
            onCancel={() => {
                setAddMilestoneModal(false);
                form.resetFields();
            }}
        >
            {/* Custom Header with Blue Background */}
            <div style={{
                backgroundColor: '#253271',
                color: 'white',
                padding: '16px 24px',
                margin: '-24px -24px 24px -24px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <h2 style={{ color: 'white', margin: 0 }}>{getModalTitle()}</h2>
                <Space>
                    <Dropdown overlay={addMenu} trigger={['click']}>
                        <PlusOutlined style={{ color: 'white' }} />
                    </Dropdown>
                    <CopyOutlined onClick={handleCopyLinkClick} style={{ color: 'white' }} />
                    <SendOutlined onClick={handleCopyLinkClick} style={{ color: 'white' }} />
                    <Button
                        icon={<CloseOutlined />}
                        onClick={() => {
                            setAddMilestoneModal(false);
                            form.resetFields();
                        }}
                        style={{ color: 'white', borderColor: 'white' }}
                        ghost
                    />
                </Space>
            </div>
            <Row gutter={24}>
                <Col span={18}>
                    {/* Main Form Section */}
                    <Form form={form} layout="vertical" name="milestone_form">
                        <Row gutter={[12, 0]}>
                            <Col span={12}>
                                <Form.Item
                                    name="name"
                                    label="Milestone Name"
                                    rules={[
                                        {
                                            required: true,
                                            message: 'Please Input Milestone name',
                                        },
                                    ]}
                                >
                                    <Input
                                        placeholder="Milestone Name"
                                        disabled={isFieldDisabled('name')}
                                        onChange={handleNameChange}
                                        style={{
                                            backgroundColor: isFieldDisabled('name') ? '#f5f5f5' : '#fafafa',
                                            color: 'rgba(0, 0, 0, 0.85)',
                                            borderColor: '#d9d9d9'
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="Owner"
                                    label="Milestone Owner"
                                    rules={[
                                        {
                                            required: true,
                                            message: 'Please Select Milestone Owner',
                                        },
                                    ]}
                                >
                                    <AutoCompleteAsync
                                        entity={obfuscatedPath}
                                        searchFields={['firstName', 'lastName']}
                                        displayLabels={['firstName', 'lastName']}
                                        outputValue="_id"
                                        disabled={isFieldDisabled('Owner')}
                                        onChange={handleOwnerChange}
                                        value={form.getFieldValue('Owner')}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item
                                    name="description"
                                    label="Description and Purpose"
                                    rules={[
                                        {
                                            required: true,
                                            message: 'Please enter description',
                                        },
                                    ]}
                                >
                                    <TextArea
                                        rows={4}
                                        placeholder="Description and Purpose"
                                        disabled={isFieldDisabled('description')}
                                        onChange={handleDescriptionChange}
                                        style={{
                                            backgroundColor: isFieldDisabled('description') ? '#f5f5f5' : '#fafafa',
                                            color: 'rgba(0, 0, 0, 0.85)',
                                            borderColor: '#d9d9d9'
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="type"
                                    label="Type of Milestones"
                                    rules={[
                                        {
                                            required: true,
                                            message: 'Please Select Type of milestone',
                                        },
                                    ]}
                                >
                                    <Select
                                        options={[
                                            { value: 'Health', label: 'Health Milestones' },
                                            { value: 'Implementation', label: 'Implementation Milestones' },
                                            { value: 'Money Step', label: 'Money Step' },
                                        ]}
                                        disabled={isFieldDisabled('type')}
                                        onChange={handleTypeChange}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="status"
                                    label="Status"
                                    rules={[{ required: true, message: 'Please Select Status' }]}
                                >
                                    <Select
                                        options={getStatusOptions()}
                                        disabled={isFieldDisabled('status')}
                                        onChange={handleStatusChange}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="startDate"
                                    label="Work Starting Date"
                                    rules={[
                                        {
                                            required: true,
                                            message: 'Please enter milestone start date',
                                        },
                                    ]}
                                >
                                    <DatePicker
                                        style={{ width: '100%' }}
                                        disabled={isFieldDisabled('startDate')}
                                        onChange={handleStartDateChange}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="endDate"
                                    label="Milestone Completion Date"
                                    rules={[
                                        {
                                            required: true,
                                            message: 'Please enter milestone end date',
                                        },
                                    ]}
                                >
                                    <DatePicker
                                        style={{ width: '100%' }}
                                        disabled={isFieldDisabled('endDate')}
                                        onChange={handleEndDateChange}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="planningSummary" label="Planning Summary">
                                    <Input disabled />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="planningCheck" label="Planning Check">
                                    <Input disabled />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                    {edit && (
                        <div style={{ marginTop: 16, textAlign: 'right' }}>
                            <Button onClick={handleOk} type="primary">
                                Close
                            </Button>
                        </div>
                    )}
                    {!edit && (
                        <div style={{ marginTop: 16, textAlign: 'right' }}>
                            <Button onClick={handleOk} type="primary">
                                Save Milestone
                            </Button>
                        </div>
                    )}
                </Col>

                <Col span={6}>
                    {/* Right Side: Comments and Activities Section */}
                    <div
                        style={{
                            paddingLeft: '16px',
                            display: 'flex',
                            flexDirection: 'column',
                            height: '400px',
                        }}
                    >
                        <Select value={selectedActivityType} onChange={setSelectedActivityType}>
                            <Option value="both">Both</Option>
                            <Option value="activities">Activities</Option>
                            <Option value="comments">Comments</Option>
                        </Select>

                        <div
                            style={{ overflowY: 'auto', flexGrow: 1, maxHeight: '300px' }}
                            ref={commentsActivitiesRef}
                        >
                            {(selectedActivityType === 'both' || selectedActivityType === 'comments') &&
                                initiative?.comments &&
                                initiative.comments.length > 0 && (
                                    <div>
                                        <h3>Comments</h3>
                                        {initiative.comments
                                            .slice()
                                            .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                                            .map((comment, index) => (
                                                <MemoizedCommentItem
                                                    key={index}
                                                    comment={comment}
                                                    currentUser={currentUser}
                                                    getUserRoleName={getUserRoleName}
                                                    getUserName={getUserName}
                                                />
                                            ))}
                                    </div>
                                )}
                            {(selectedActivityType === 'both' || selectedActivityType === 'activities') && (
                                <div style={{ marginBottom: '8px' }}>
                                    <h3>Activities</h3>
                                    {milestoneActivities.map((activity, index) => (
                                        <MemoizedActivityItem
                                            key={activity.id || index}
                                            activity={activity}
                                            onShowChangeDetails={showChangeDetailsModal}
                                            users={users}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Modal for displaying change details */}
                        <Modal
                            title={`Change Details for ${changeDetails?.fieldName}`}
                            visible={visibleChangeDetails}
                            onCancel={handleCloseChangeDetailsModal}
                            footer={null}
                        >
                            {changeDetails && (
                                <div>
                                    <p>
                                        <strong>From:</strong> {JSON.stringify(changeDetails.previous)}
                                    </p>
                                    <p>
                                        <strong>To:</strong> {JSON.stringify(changeDetails.new)}
                                    </p>
                                </div>
                            )}
                        </Modal>

                        {/* Comment Input Section */}
                        <div style={{ display: 'flex', alignItems: 'center', marginTop: '8px' }}>
                            <TextArea
                                value={newComment}
                                onChange={(e) => setNewComment(e.target.value)}
                                style={{ flex: 1, marginRight: '8px' }}
                                autoSize={{ minRows: 1, maxRows: 3 }}
                            />
                            <Button type="primary" icon={<SendOutlined />} onClick={handleAddComment} />
                        </div>
                    </div>
                </Col>
            </Row>
        </Modal>
    );
};

export default AddMilestoneModal;